import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

interface SensitiveDataAlertProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataType: 'stripe' | 'generic';
  keyType?: 'live' | 'test' | 'restricted';
  onProceedAnyway?: () => void;
}

export function SensitiveDataAlert({
  open,
  onOpenChange,
  dataType,
  keyType,
  onProceedAnyway,
}: SensitiveDataAlertProps) {
  const isStripe = dataType === 'stripe';
  const isLiveKey = keyType === 'live';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-full ${isLiveKey ? 'bg-red-500/10' : 'bg-orange-500/10'}`}>
              <AlertTriangle className={`h-6 w-6 ${isLiveKey ? 'text-red-500' : 'text-orange-500'}`} />
            </div>
            <AlertDialogTitle className="text-xl">
              {isLiveKey ? 'Danger: Live Secret Key Detected!' : 'Warning: Secret Key Detected'}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base space-y-3">
            {isStripe ? (
              <>
                <p className="font-medium text-foreground">
                  {isLiveKey 
                    ? '⚠️ You appear to be trying to paste a LIVE Stripe secret key into this chat.'
                    : 'You appear to be trying to paste a Stripe secret key into this chat.'
                  }
                </p>
                <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                  <p className="font-semibold text-foreground">Why this is dangerous:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Secret keys should <strong>never</strong> be shared with anyone or any service</li>
                    <li>Your messages may be processed by third-party AI providers</li>
                    <li>Secret keys grant access to your Stripe account and payment data</li>
                    {isLiveKey && (
                      <li className="text-red-600 dark:text-red-400 font-semibold">
                        LIVE keys have access to real customer data and real money!
                      </li>
                    )}
                  </ul>
                </div>
                <div className="bg-blue-500/10 p-4 rounded-lg space-y-2 border border-blue-500/20">
                  <p className="font-semibold text-foreground">✓ Safe alternative:</p>
                  <p className="text-sm">
                    Your Stripe integration is already configured in{' '}
                    <a 
                      href="/settings/integrations" 
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      Settings → Integrations
                    </a>
                    . The AI can access your Stripe account securely through OAuth without needing your secret key.
                  </p>
                </div>
                {isLiveKey && (
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                    If you've already shared a live key somewhere, please rotate it immediately in your Stripe dashboard.
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="font-medium text-foreground">
                  You appear to be trying to paste a secret key or credential into this chat.
                </p>
                <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                  <p className="font-semibold text-foreground">Why this is dangerous:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Secret credentials should never be shared with AI assistants</li>
                    <li>Your messages may be processed by third-party AI providers</li>
                    <li>Exposed credentials could compromise your accounts and data</li>
                  </ul>
                </div>
                <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                  <p className="text-sm">
                    💡 <strong>Tip:</strong> Store sensitive credentials as environment variables or use secure configuration management instead of pasting them in chat.
                  </p>
                </div>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel className="w-full sm:w-auto">
            Cancel and Remove Key
          </AlertDialogCancel>
          {onProceedAnyway && !isLiveKey && (
            <AlertDialogAction
              onClick={() => {
                onProceedAnyway();
                onOpenChange(false);
              }}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600"
            >
              I Understand the Risks
            </AlertDialogAction>
          )}
          {isLiveKey && (
            <AlertDialogAction
              asChild
            >
              <a 
                href="/settings/integrations"
                className="w-full sm:w-auto"
              >
                Go to Integrations Settings
              </a>
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
